import React, { useEffect, useState } from 'react';
import { Loader, AlertCircle, ExternalLink } from 'lucide-react';
import AudioPlayer from '../AudioPlayer';
import VideoPlayer from '../VideoPlayer';
import { getPublicShare } from '../../utils/sharingDb';

/**
 * Public-facing viewer for a single shared recording.
 *
 * Rendered as the entire page when `index.js` detects a `/r/:shareId`
 * path. Does NOT load the authenticated app shell — visitors don't need
 * to be signed in. Drops the recording into the existing `AudioPlayer`
 * or `VideoPlayer` (read-only) and decorates with a small FlowFrame
 * brand mark in the corner.
 *
 * States:
 *   loading    — fetching the share doc
 *   missing    — share doc not found, revoked, or owner deleted source
 *   ready      — player rendered
 */
const PublicRecordingView = ({ shareId }) => {
  const [state, setState] = useState({ status: 'loading', share: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const share = await getPublicShare(shareId);
        if (cancelled) return;
        if (!share) {
          setState({ status: 'missing', share: null });
          return;
        }
        setState({ status: 'ready', share });
      } catch (err) {
        console.error('PublicRecordingView load failed:', err);
        if (!cancelled) setState({ status: 'missing', share: null });
      }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  // Update the document title to the recording title at runtime. Social
  // crawlers don't run JS so this doesn't affect og:title (those stay
  // static FlowFrame-branded per tier 1), but it improves the browser
  // tab, system share-sheet defaults, and link previews in clients that
  // do execute JS (some chat apps, in-app browsers).
  useEffect(() => {
    if (state.status === 'ready' && state.share?.title) {
      const prev = document.title;
      document.title = `${state.share.title} · FlowFrame`;
      return () => { document.title = prev; };
    }
  }, [state]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Top bar — FlowFrame brand + sign-up CTA */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center space-x-2 group">
            <img
              src="/logo192.png"
              alt="FlowFrame"
              className="w-8 h-8"
            />
            <span className="font-semibold text-gray-800 group-hover:text-accent-600 transition-colors">
              FlowFrame
            </span>
          </a>
          <a
            href="/?signup=1"
            className="inline-flex items-center text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 px-3 py-1.5 rounded-md transition-colors"
          >
            Try FlowFrame free
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {state.status === 'loading' && (
          <div className="flex items-center justify-center py-16">
            <Loader className="w-6 h-6 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-500">Loading recording…</span>
          </div>
        )}

        {state.status === 'missing' && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-gray-800 mb-2">
              This recording is no longer available
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              The share link may have been revoked or the recording has been deleted.
            </p>
            <a
              href="/"
              className="inline-flex items-center text-sm font-medium text-accent-600 hover:underline"
            >
              Visit FlowFrame
              <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </div>
        )}

        {state.status === 'ready' && state.share && (
          <PublicRecordingBody share={state.share} />
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-4 py-8 text-center text-xs text-gray-400">
        Shared via <a href="/" className="hover:text-accent-600">FlowFrame</a>
      </footer>
    </div>
  );
};

/**
 * The actual share body once the share doc has loaded. Split out so the
 * conditional player render reads cleanly.
 */
const PublicRecordingBody = ({ share }) => {
  const isVideo = share.mediaType === 'video';
  const title = share.title || 'FlowFrame recording';

  // Build a recording-shaped object the existing players already consume.
  const recordingForPlayer = isVideo
    ? {
        id: share.recordingId,
        mediaType: 'video',
        status: 'ready',
        hlsPath: share.hlsPath,
        posterPath: share.posterPath,
        storagePath: share.storagePath,
        userId: share.ownerUid,
        // Skip the live mediaRecordings subscription — public viewers
        // can't read that doc. Pass recordingDocPath as null to opt out.
        recordingDocPath: null
      }
    : null;

  return (
    <article>
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-800">{title}</h1>
        {share.sessionTitle && (
          <p className="text-sm text-gray-500 mt-1">{share.sessionTitle}</p>
        )}
      </div>

      {/* Player wrapper — relative so we can drop the FlowFrame mark in
          the corner without disturbing the player's own layout. */}
      <div className="relative">
        {isVideo ? (
          <VideoPlayer recording={recordingForPlayer} className="w-full" />
        ) : (
          <AudioPlayer
            audioUrl={share.audioUrl}
            recordingId={share.recordingId}
            sessionId={null}
            userId={share.ownerUid}
            initialRegions={[]}
            initialComments={[]}
            initialMarkers={[]}
            compact={false}
            readOnly={true}
          />
        )}

        {/* Subtle brand mark — anchored to the top-right of the player
            wrapper so it shows regardless of which player rendered. */}
        <div
          className="absolute top-2 right-2 flex items-center bg-white/70 backdrop-blur-sm rounded-full pl-1 pr-2 py-0.5 shadow-sm pointer-events-none"
          aria-hidden
        >
          <img src="/logo192.png" alt="" className="w-4 h-4" />
          <span className="ml-1 text-[10px] font-semibold text-gray-700">FlowFrame</span>
        </div>
      </div>

      {/* Community CTA placeholder — wired up in step 6. */}
      <div className="mt-6 text-center">
        <a
          href="/?tab=community"
          className="inline-flex items-center text-sm font-medium text-accent-600 hover:underline"
        >
          Discuss this on FlowFrame
          <ExternalLink className="w-4 h-4 ml-1" />
        </a>
      </div>
    </article>
  );
};

export default PublicRecordingView;
